import { isObservable, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, startWith, tap } from 'rxjs/operators';

export enum ApiResponseStatus {
  Loading = 'loading',
  Success = 'success',
  Failure = 'failure',
}

export interface ApiResponse<TData> {
  data: TData;
  status: ApiResponseStatus;
  error: unknown;
}

export function handleApiResponse<TData>(
  apiCall: Observable<TData>,
  initialValue: TData,
  errObsFactoryOrRethrow?:
    | true
    | ((err: unknown) => unknown | Observable<unknown>)
): Observable<ApiResponse<TData>> {
  return apiCall.pipe(
    map((data) => ({
      status: ApiResponseStatus.Success,
      data,
      error: '',
    })),
    startWith({
      status: ApiResponseStatus.Loading,
      data: initialValue,
      error: '',
    }),
    catchError((err) => {
      const defaultFailureResponse = {
        status: ApiResponseStatus.Failure,
        data: initialValue,
      };

      if (errObsFactoryOrRethrow == null) {
        return of<ApiResponse<TData>>({
          ...defaultFailureResponse,
          error: err.message || err.error || err.toString(),
        });
      }

      if (typeof errObsFactoryOrRethrow === 'function') {
        const error = errObsFactoryOrRethrow(err);
        if (isObservable(error)) {
          return error.pipe(
            map<unknown, ApiResponse<TData>>((e) => ({
              ...defaultFailureResponse,
              error: e,
            }))
          );
        }

        return of<ApiResponse<TData>>({
          ...defaultFailureResponse,
          error,
        });
      }

      return throwError(err);
    }),
    shareReplay(1)
  );
}
